import type { MaterializedMenuProduct } from '@iheartjane/data-access';
import type { Nullable, SpecialType } from '@iheartjane/types';

import type { CustomLanguage } from '@jane/bloom/types/schemas/bloom/customLanguage';
import type { StoreSpecials } from '@jane/bloom/types/schemas/marketplace/storeSpecials';
import { getDefaultProductPhoto } from '@jane/bloom/utils/product/getDefaultProductPhoto';
import { getMiniSpecialBadgeLabel } from '@jane/bloom/viewModels/getPrice';
import { generateCustomLabel } from '@jane/bloom/viewModels/labels';
import { imageSrcSet } from '@jane/bloom/viewModels/productImage';
import { productImageDimensions } from '@jane/bloom/viewModels/productImage.constants';

export interface AutocompleteProduct extends MaterializedMenuProduct {
  baseImage: string;
  imageSrcSet?: { fallbackSrc: string; sizes: string; srcSet: string };
  lineage: string;
  specialLabel: Nullable<string>;
  specialType: Nullable<SpecialType>;
}

type BuildAutocompleteProductArgs = {
  customLanguage: CustomLanguage;
  product: MaterializedMenuProduct;
  specials: Nullable<StoreSpecials>;
};

export const buildAutocompleteProduct = ({
  customLanguage,
  product,
  specials,
}: BuildAutocompleteProductArgs): AutocompleteProduct => {
  const image_url =
    product.image_urls[0] ||
    getDefaultProductPhoto({
      category: product.category,
      kind: product.kind,
      productRootSubtype: product.root_subtype,
      productType: product.type,
    });

  const productImageSrcSet = imageSrcSet(
    image_url,
    [productImageDimensions.searchResult.width],
    productImageDimensions.searchResult.width
  );

  const specialLabel = getMiniSpecialBadgeLabel(product);
  const specialType =
    specials?.find((special) => special.id === product.special_id)
      ?.special_type || null;

  return {
    ...product,
    baseImage:
      product.image_urls[0] ||
      getDefaultProductPhoto({
        category: product.category,
        kind: product.kind,
        productRootSubtype: product.root_subtype,
        productType: product.type,
      }),
    imageSrcSet: productImageSrcSet,
    lineage: generateCustomLabel(product.category || '', customLanguage),
    specialLabel,
    specialType,
  };
};
